import { Manager, logger, appDataCacheUtils } from "../../../core";
import { determineAppAccess } from "../../../core/utils/appAccessUtils";
import { navigationManager } from "../../../managers/navigation-manager";

export class AppSettingsManager extends Manager {
  async initStates() {
    logger.log(
      {
        tags: "manager|app-settings|init",
        color1: "cyan",
      },
      "Initializing AppSettings manager states"
    );

    logger.log(
      {
        tags: "manager|app-settings|ready",
        color1: "lime",
      },
      "AppSettings manager initialized (lazy load per app)"
    );
  }

  async loadApp(appId, userId, userInfo, options = {}) {
    const onProgress = options.onProgress;
    const report = (percent, message) => {
      if (typeof onProgress === "function") onProgress({ percent, message });
    };

    try {
      if (!appId) {
        return { error: 'missing_app_id' };
      }
      if (!userId) {
        // auth layer should handle redirect; mark unauth for UI
        return { error: 'unauthorized' };
      }
      const currentUserId = userId || null;

      logger.log(
        { tags: "manager|app-settings|load", color1: "cyan" },
        "Loading app settings",
        { appId, userId: currentUserId }
      );

      report(0, "Fetching app data...");
      const appResult = await appDataCacheUtils.fetchAppData(appId);
      const appData = appResult?.data || null;
      if (!appResult?.ok || !appData?.app) {
        return { error: appData?.error || 'not_found' };
      }

      const app = appData.app;
      app.id = app._id?.toString?.() || app.id;
      app.createdAtFormatted = this.formatDate(app.createdAt);
      app.modifiedAtFormatted = this.formatDate(app.modifiedAt);
      app.owner = app.ownerId ? app.ownerId.toString?.() : null;
      app.ownerUsername = app.ownerUsername || '';
      app.allowRemixing = app.allowRemixing !== false; // Default to true if undefined or null
      report(25, "App data loaded");

      report(40, "Checking access...");
      const membershipRes = await fetch(
        `/api/app-memberships/by-app?appId=${encodeURIComponent(appId)}&userId=${encodeURIComponent(currentUserId)}`,
        { credentials: 'include' }
      );
      const membershipData = membershipRes.ok ? await membershipRes.json() : null;
      let membership = membershipData?.membership || null;

      // Use centralized access determination (userInfo carries role for site-admin checks)
      const access = determineAppAccess(
        app,
        userInfo || { _id: currentUserId, id: currentUserId, userId: currentUserId },
        membership
      );
      if (!access.canView) {
        return { error: 'forbidden' };
      }

      // For public viewers, create a virtual membership
      if (access.isPublicViewer) {
        membership = { role: 'member', isPublicView: true };
      }

      // Extract access properties from centralized utility
      const isOwner = access.isOwner;
      const isAdmin = access.isAdmin;
      const isMember = access.isMember;
      const isSiteAdmin = access.isSiteAdmin;
      this.isAuthorized = true; // any membership is authorized to view basic settings

      report(60, "Loading members...");
      // Fetch all memberships (needed to resolve owner username)
      let members = [];
      if (!membership?.isPublicView) {
        const allRes = await fetch(
          `/api/app-memberships/by-app/all?appId=${encodeURIComponent(appId)}`,
          { credentials: 'include' }
        );
        if (allRes.ok) {
          const allData = await allRes.json();
          members = allData?.memberships || [];
        }
      }

      // Resolve owner username strictly (no fallbacks)
      if (!app.ownerUsername) {
        const ownerMember = members.find(
          (m) =>
            m.role === 'owner' ||
            (app.ownerId && String(m.userId) === String(app.ownerId))
        );
        if (ownerMember) {
          app.ownerUsername = ownerMember.username || ownerMember.name || '';
        }
      }
      logger.log(
        { tags: "manager|app-settings|authz", color1: this.isAuthorized ? "lime" : "red" },
        "Authz check",
        {
          appId: app.id,
          ownerId: app.ownerId,
          userId: currentUserId,
          role: membership?.role,
          isOwner,
          isAdmin,
          isSiteAdmin,
          membership
        }
      );
      // Reorder members: current user first, then by email
      members = members
        .map(m => ({
          ...m,
          email: m.email || '',
          username: m.username || m.name || '',
          name: m.name || '',
          status: m.status || 'active'
        }))
        .sort((a, b) => (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' }));

      if (currentUserId) {
        members = members.sort((a, b) => {
          const aSelf = String(a.userId) === String(currentUserId);
          const bSelf = String(b.userId) === String(currentUserId);
          if (aSelf && !bSelf) return -1;
          if (bSelf && !aSelf) return 1;
          return 0;
        });
      }

      report(85, "Preparing settings...");
      report(100, "Ready");

      return {
        error: null,
        app,
        membership,
        members,
        isAuthorized: this.isAuthorized,
        isOwner,
        isAdmin,
        isMember,
        isSiteAdmin,
        isPublicViewer: access.isPublicViewer
      };
    } catch (error) {
      logger.error(
        {
          tags: "manager|app-settings|error",
          color1: "red",
          includeSource: true,
        },
        "Failed to load app settings",
        error
      );
      return { error: 'error' };
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  async togglePublic(appId, nextValue) {
    if (!appId) return null;
    try {
      const response = await fetch(`/api/apps/${appId}/public`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: nextValue }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to update visibility");
      }
      const updatedApp = data.app || null;
      if (updatedApp) {
        this.app = updatedApp;
        appDataCacheUtils.clear(appId);
      }
      logger.log(
        {
          tags: "manager|app-settings|action",
          color1: "cyan",
        },
        `App visibility set to ${updatedApp?.isPublic ? "public" : "private"}`
      );
      return updatedApp;
    } catch (error) {
      logger.error(
        {
          tags: "manager|app-settings|action",
          color1: "red",
          includeSource: true,
        },
        "Failed to update app visibility",
        error
      );
      throw error;
    }
  }

  async toggleRemixing(appId, nextValue) {
    if (!appId) return null;
    try {
      const response = await fetch(`/api/apps/${appId}/remixing`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: nextValue }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to update remixing setting");
      }
      const updatedApp = data.app || null;
      if (updatedApp) {
        this.app = updatedApp;
        // Note: No cache clearing needed for remixing as it's not cached like other data
      }
      logger.log(
        {
          tags: "manager|app-settings|action",
          color1: "cyan",
        },
        `App remixing set to ${updatedApp?.allowRemixing ? "enabled" : "disabled"}`
      );
      return updatedApp;
    } catch (error) {
      logger.error(
        {
          tags: "manager|app-settings|action",
          color1: "red",
          includeSource: true,
        },
        "Failed to update app remixing setting",
        error
      );
      throw error;
    }
  }

  async togglePublish(appId, nextValue) {
    if (!appId) return null;
    try {
      const response = await fetch(`/api/apps/${appId}/publish`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: nextValue }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to update publish setting");
      }
      const updatedApp = data.app || null;
      if (updatedApp) {
        this.app = updatedApp;
        appDataCacheUtils.clear(appId);
      }
      logger.log(
        {
          tags: "manager|app-settings|action",
          color1: "cyan",
        },
        `App publish to community set to ${updatedApp?.isPublishedToCommunity ? "enabled" : "disabled"}`
      );
      return updatedApp;
    } catch (error) {
      logger.error(
        {
          tags: "manager|app-settings|action",
          color1: "red",
          includeSource: true,
        },
        "Failed to update app publish setting",
        error
      );
      throw error;
    }
  }

  async openPlayspace(appId) {
    logger.log(
      {
        tags: "manager|app-settings|action",
        color1: "cyan",
      },
      `Opening playspace for app: ${appId}`
    );
    if (appId) {
      await navigationManager.navigateToPath(`/playspace/${appId}`);
    } else {
      await navigationManager.navigateTo("playspace");
    }
  }

  async inviteMember(appId, email, role = 'member') {
    if (!appId || !email) return;
    try {
      await fetch('/api/app-memberships/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appId, email, role })
      });
      logger.log({ tags: 'manager|app-settings|invite', color1: 'cyan' }, 'Invite submitted', {
        appId,
        email,
        role
      });
    } catch (error) {
      logger.error({ tags: 'manager|app-settings|invite', color1: 'red' }, 'Invite failed', error);
    }
  }

  async updateMemberRole(appId, targetUserId, role) {
    if (!appId || !targetUserId || !role) return;
    try {
      await fetch('/api/app-memberships/role', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appId, targetUserId, role }),
      });
      logger.log(
        { tags: 'manager|app-settings|role', color1: 'cyan' },
        'Updated member role',
        { appId, targetUserId, role }
      );
    } catch (error) {
      logger.error({ tags: 'manager|app-settings|role', color1: 'red' }, 'Role update failed', error);
    }
  }

  async removeMember(appId, targetUserId) {
    if (!appId || !targetUserId) return;
    try {
      await fetch('/api/app-memberships', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appId, targetUserId }),
      });
      logger.log(
        { tags: 'manager|app-settings|remove', color1: 'cyan' },
        'Removed member',
        { appId, targetUserId }
      );
    } catch (error) {
      logger.error({ tags: 'manager|app-settings|remove', color1: 'red' }, 'Remove member failed', error);
    }
  }

  async openWorkspace(projectId) {
    logger.log(
      {
        tags: "manager|app-settings|action",
        color1: "cyan",
      },
      `Opening workspace for app: ${projectId}`
    );
    if (projectId) {
      await navigationManager.navigateToPath(`/workspace/${projectId}`);
    } else {
      await navigationManager.navigateTo("workspace");
    }
  }

  async renameApp(appId, newName) {
    if (!appId || !newName || !newName.trim()) return null;
    try {
      const res = await fetch(`/api/apps/${appId}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to rename app');
      }
      return data.app;
    } catch (error) {
      logger.error(
        { tags: "manager|app-settings|rename", color1: "red" },
        "Rename app failed",
        error
      );
      return null;
    }
  }

  async updateAppDescription(appId, description) {
    if (!appId) return null;
    try {
      const res = await fetch(`/api/apps/${appId}/description`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description: description ?? '' }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to update description');
      }
      return data.app;
    } catch (error) {
      logger.error(
        { tags: "manager|app-settings|description", color1: "red" },
        "Update app description failed",
        error
      );
      return null;
    }
  }

  async deleteApp(appId) {
    if (!appId) return false;
    try {
      const res = await fetch(`/api/apps/${appId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to delete app');
      }
      return true;
    } catch (error) {
      logger.error(
        { tags: "manager|app-settings|delete", color1: "red" },
        "Delete app failed",
        error
      );
      return false;
    }
  }

  async transferOwnership(appId, newOwnerId) {
    if (!appId || !newOwnerId) return false;
    try {
      const res = await fetch(`/api/apps/${appId}/transfer-ownership`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newOwnerId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to transfer ownership');
      }
      return true;
    } catch (error) {
      logger.error(
        { tags: "manager|app-settings|transfer", color1: "red" },
        "Transfer ownership failed",
        error
      );
      return false;
    }
  }

  /**
   * Fetch site-admin-only app insights (sync flags, etc.)
   * @returns {Promise<{ appId: string, hasEverSyncedFiles: boolean, hasNonOwnerSyncedFiles: boolean }>}
   */
  async fetchAdminAppInsights(appId) {
    if (!appId) {
      throw new Error("App id is required");
    }
    const response = await fetch(`/api/admin/apps/${encodeURIComponent(appId)}/insights`, {
      method: "GET",
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || "Failed to fetch admin app insights");
    }
    return {
      appId: data.appId,
      hasEverSyncedFiles: data.hasEverSyncedFiles === true,
      hasNonOwnerSyncedFiles: data.hasNonOwnerSyncedFiles === true,
    };
  }
}

